<!--
  The Settings section for local agents (Remote Relay, docs/remote-relay.md):
  connections waiting for approval, the agents this browser is paired with
  and the subjects (pull requests, for now) each may read (scope and revoke
  per grant),
  disconnecting or forgetting one, connecting another, and this browser's
  agent identity. The pairings and grants are this browser's
  (state/relayAgents.svelte.ts). Each agent says when it was last heard
  from; one not heard from for an hour (`stale`) says what it can still read.
-->
<script lang="ts">
  import { tick } from 'svelte';
  import { MOCK } from '../mock/flag';
  import RelayApproval from './RelayApproval.svelte';
  import RelayConnect from './RelayConnect.svelte';
  import { harnessText } from '../logic/relayHarness';
  import { kindOf } from '../api/relay-grants';
  import { matchKey, type SubjectKey } from '../api/relaySubject';
  import { lastSeenText, subjectHref } from '../logic/relayText';
  import RelayRefused from './RelayRefused.svelte';
  import RelayScope from './RelayScope.svelte';
  import SettingRow from './SettingRow.svelte';
  import { showFingerprint, resetIdentity, type RelayCategory, type RelayConnection } from '../api/relay';
  import { relayAgents } from '../state/relayAgents.svelte';
  let error = $state('');
  let busy = $state(false);
  let news = $state('');
  /** The connection whose disconnect could not reach its computer, and why. */
  let unreachable = $state<{ id: string; text: string } | null>(null);
  /** A scope change on its way (`connection\0subject`), shown until the grants are read again. */
  let pending = $state<Record<string, RelayCategory[]>>({});
  const why = (e: unknown) => (e instanceof Error ? e.message : String(e));
  const grantKey = (connection: string, subject: SubjectKey) => `${connection}\0${matchKey(subject)}`;
  let newsLine: HTMLElement | undefined = $state();
  /** Runs `fn`, then says `done`; with `refocus`, focus moves to that line (the control used went away with what it acted on). */
  async function act(fn: () => Promise<void>, done: string | (() => string) = '', refocus = false) {
    if (busy) return;
    busy = true; error = ''; news = '';
    try {
      await fn();
      news = typeof done === 'string' ? done : done();
      if (refocus) { await tick(); newsLine?.focus(); }
    } catch (e) { error = why(e); } finally { busy = false; }
  }
  function setScope(c: RelayConnection, subject: SubjectKey, scope: RelayCategory[]) {
    const k = grantKey(c.id, subject);
    pending[k] = scope;
    void act(() => relayAgents.grant(c.id, subject, scope), `Changed what ${c.name} may read of ${subject}.`).finally(() => delete pending[k]);
  }
  /** Sends a refused grant again as it was, then says what came of it (refused again, maybe) where focus goes. */
  function grantAgain(c: RelayConnection, subject: SubjectKey, scope: readonly RelayCategory[]) {
    void act(() => relayAgents.grant(c.id, subject, [...scope]), () => {
      const g = relayAgents.grantsOn(subject).find((x) => x.connection === c.id);
      return g?.status === 'refused' ? `${c.name}’s computer refused the grant on ${subject} again: ${g.reason}` : `${c.name} can read ${subject} again.`;
    }, true);
  }
  function revokeGrant(c: RelayConnection, subject: SubjectKey) {
    void act(() => relayAgents.revokeGrant(c.id, subject), `${c.name} can no longer read ${subject}.`, true);
  }
  async function disconnect(c: RelayConnection) {
    if (busy) return;
    if (!confirm(`Disconnect ${c.name} and delete its task history? You will need a new link from your agent to reconnect.`)) return;
    busy = true; error = ''; news = ''; unreachable = null;
    try { await relayAgents.unpair(c.id); news = `Disconnected ${c.name}.`; await tick(); newsLine?.focus(); } catch (e) { unreachable = { id: c.id, text: why(e) }; } finally { busy = false; }
  }
  function forgetHere(c: RelayConnection) {
    void act(async () => { await relayAgents.forget(c.id); unreachable = null; }, `Forgot ${c.name} on this browser.`, true);
  }
  async function reset() {
    if (busy) return;
    if (!confirm('Reset this browser’s agent identity? Every agent connection in this browser and its history are removed. Your computers keep their grants to the old identity until they are revoked there. Connect again with a new link from your agent.')) return;
    await act(async () => { await resetIdentity(); unreachable = null; await relayAgents.refresh(); }, 'This browser has a new agent identity. Connect your agents again with new links.');
  }
</script>

<section class="settings-group" aria-labelledby="settings-relay">
  <h2 id="settings-relay">Local agents</h2>
  <p class="group-note">Kept in this browser. Agents on your computer read the pull requests you grant them, and you can send them review threads; your browser and the agent encrypt their connection end to end.</p>
  {#if news}<p class="banner settings-status relay-banner" role="status" tabindex="-1" bind:this={newsLine}>{news}</p>{/if}
  {#if error}<p class="setting-problem relay-banner" role="alert">{error}</p>{:else if relayAgents.error}<p class="setting-problem relay-banner" role="alert">{relayAgents.error}</p>{/if}
  {#if !MOCK}<RelayApproval />{/if}
  <div class="card settings-rows">
    {#each relayAgents.connections as c (c.id)}
      {@const grants = relayAgents.grants.filter((g) => g.connection === c.id)}
      <SettingRow id={`relay-${c.id}`} name={c.name}>
        {#snippet description()}{harnessText(c.harness)} · fingerprint <span class="fingerprint">{showFingerprint(c.peer)}</span> · {lastSeenText(relayAgents.lastSeen(c.id), relayAgents.now)}{/snippet}
        {#snippet control()}
          <button class="btn small danger" aria-disabled={busy ? 'true' : undefined} onclick={() => disconnect(c)}>Disconnect…</button>
        {/snippet}
        {#snippet notes()}
          {#if unreachable?.id === c.id}
            <p class="setting-problem" role="alert">{unreachable.text}</p>
            <p>You can forget this connection on this browser only. Its history here is deleted, but your computer keeps this browser’s access until it is revoked there.</p>
            <p><button class="btn small" aria-disabled={busy ? 'true' : undefined} onclick={() => forgetHere(c)}>Forget on this browser only</button></p>
          {/if}
          {#if relayAgents.stale(c.id)}
            {@const reads = grants.filter((g) => g.status === 'granted').length}
            <p class="stale">{reads ? `Not heard from in the last hour, and it can still read ${reads === 1 ? '1 pull request' : `${reads} pull requests`}. Revoke what it no longer needs, or disconnect it if you’re done with it.` : 'Not heard from in the last hour, and it can read no pull request. Disconnect it if you’re done with it.'}</p>
          {/if}
          {#if grants.length}
            <ul class="grants" aria-label="What {c.name} may read">
              {#each grants as g (matchKey(g.subject))}
                {@const href = subjectHref(g.subject)}
                <li>
                  {#if href}<a {href}>{g.subject}</a>{:else}<span>{g.subject}</span>{/if}
                  {#if g.status === 'refused'}
                    <RelayRefused grant={g} label="Grant {g.subject} again" disabled={busy} onretry={() => grantAgain(c, g.subject, g.scope)} />
                  {:else}
                    <RelayScope kind={kindOf(g.subject)} legend="What {c.name} may read of {g.subject}" scope={pending[grantKey(c.id, g.subject)] ?? g.scope} disabled={busy} onchange={(s) => setScope(c, g.subject, s)} />
                  {/if}
                  <button class="btn small danger" aria-label="Revoke {g.subject}" aria-disabled={busy ? 'true' : undefined} onclick={() => revokeGrant(c, g.subject)}>Revoke</button>
                </li>
              {/each}
            </ul>
          {:else}
            <p class="muted">No pull request granted. Grant one from its page, under Local agents.</p>
          {/if}
          <details>
            <summary>Connection details</summary>
            <p>The agent’s identity is pinned in this browser. A different identity requires a new connection.</p>
            <p><code>{c.peer}</code></p>
            <p>Pull request data and review requests travel encrypted to this agent. The relay sees connection metadata. Changeling still handles GitHub data and any replies you choose to publish. The agent’s model provider processes what it reads.</p>
            <p>This protection trusts the app running in your browser and the software on your computer.</p>
          </details>
        {/snippet}
      </SettingRow>
    {:else}
      {#if relayAgents.loaded || MOCK}<p class="setting-row relay-empty">No agents connected in this browser.</p>{/if}
    {/each}
    <SettingRow id="relay-connect" name="Connect an agent">
      {#snippet description()}{MOCK ? 'Agent connections are available in a signed-in Changeling instance.' : 'Choose Connect my agent on a pull request, or give your agent this instruction and confirm it by its fingerprint on the page it opens.'}{/snippet}
      {#snippet control()}{/snippet}
      {#snippet notes()}{#if !MOCK}<RelayConnect titled={false} />{/if}{/snippet}
    </SettingRow>
    {#if !MOCK}
      <SettingRow id="relay-identity" name="This browser’s agent identity">
        {#snippet description()}Your agents recognize this browser by a key kept here. Reset it if you think the key was copied: every connection in this browser is removed.{/snippet}
        {#snippet control()}
          <button class="btn small danger" aria-disabled={busy ? 'true' : undefined} onclick={reset}>Reset…</button>
        {/snippet}
      </SettingRow>
    {/if}
  </div>
</section>

<style>
  .relay-banner { margin: 0 0 10px; }
  .stale { margin: 0 0 8px; font-size: 13px; color: var(--muted); }
  .relay-empty { display: block; margin: 0; color: var(--muted); font-size: 13px; }
  .fingerprint { font-family: ui-monospace, SFMono-Regular, monospace; color: var(--fg); }
  code { overflow-wrap: anywhere; font-size: 12px; }
  .grants { list-style: none; margin: 8px 0 0; padding: 0; display: grid; gap: 12px; font-size: 13px; }
  .grants li { display: grid; gap: 6px; justify-items: start; padding: 10px 12px; background: var(--code); border-radius: 8px; }
  .grants a { overflow-wrap: anywhere; }
  details { margin-top: 8px; font-size: 13px; }
  details > p { margin: 6px 0 0; line-height: 1.5; }
  .btn[aria-disabled='true'] { opacity: .45; cursor: default; }
</style>
